import { BookOpen, Heart, ShoppingCart, Star } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Product } from '../../../shared/types';

interface CustomerOverviewProps {
  myBooks?: Product[];
  purchasedProducts?: Product[];
  cartItems?: Product[];
  favoriteProducts?: Product[];
  cartCount?: number;
  wishlistCount?: number;
}

const CustomerOverview = ({
  myBooks = [],
  purchasedProducts = [],
  cartItems = [],
  favoriteProducts = [],
  cartCount = 0,
}: CustomerOverviewProps) => {
  const { t } = useTranslation();

  const stats = useMemo(
    () => [
      {
        id: 'my-books',
        label: 'My Books',
        value: myBooks?.length ?? 0,
        icon: BookOpen,
        color: 'bg-blue-100 text-blue-600',
        link: '/dashboard/library',
      },
      {
        id: 'purchased',
        label: 'Purchased',
        value: purchasedProducts?.length ?? 0,
        icon: ShoppingCart,
        color: 'bg-emerald-100 text-emerald-600',
        link: '/dashboard/library',
      },
      {
        id: 'cart',
        label: 'In Cart',
        value: cartCount ?? 0,
        icon: ShoppingCart,
        color: 'bg-orange-100 text-orange-600',
        link: '/dashboard/cart',
      },
      {
        id: 'favorites',
        label: 'Favorites',
        value: favoriteProducts?.length ?? 0,
        icon: Heart,
        color: 'bg-rose-100 text-rose-600',
        link: '/dashboard/wishlist',
      },
    ],
    [myBooks?.length, purchasedProducts?.length, cartCount, favoriteProducts?.length],
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.id}
              to={stat.link}
              className="group rounded-xl border border-slate-200 bg-white p-4 transition hover:border-orange-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className={`rounded-lg p-2 ${stat.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {(myBooks?.length ?? 0) > 0 && (
        <article className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-slate-900">My Books</h3>
            <Link to="/dashboard/library" className="text-xs font-semibold text-orange-600 hover:text-orange-700">
              View all →
            </Link>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {myBooks?.slice(0, 3).map((book) => (
              <Link
                key={book?.id}
                to={`/products/${book?.id}`}
                className="group overflow-hidden rounded-lg border border-slate-200 transition hover:border-orange-300 hover:shadow-md"
              >
                <img
                  src={book?.imageUrl || ''}
                  alt={book?.nameKey ? t(book.nameKey) : 'Book'}
                  className="h-32 w-full object-cover transition group-hover:scale-105"
                />
                <div className="p-2">
                  <p className="text-xs font-semibold text-orange-600">{book?.categoryKey ? t(book.categoryKey) : 'Book'}</p>
                  <p className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900">{book?.nameKey ? t(book.nameKey) : 'Untitled'}</p>
                  <p className="mt-1 text-sm font-bold text-slate-700">$ {(book?.price ?? 0).toFixed(0)}</p>
                </div>
              </Link>
            ))}
          </div>
        </article>
      )}

      {(purchasedProducts?.length ?? 0) > 0 && (
        <article className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-slate-900">Purchased Products</h3>
            <Link to="/dashboard/library" className="text-xs font-semibold text-orange-600 hover:text-orange-700">
              View all →
            </Link>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {purchasedProducts?.slice(0, 3).map((product) => (
              <Link
                key={product?.id}
                to={`/products/${product?.id}`}
                className="group overflow-hidden rounded-lg border border-emerald-200 bg-emerald-50 transition hover:border-emerald-400 hover:shadow-md"
              >
                <img
                  src={product?.imageUrl || ''}
                  alt={product?.nameKey ? t(product.nameKey) : 'Product'}
                  className="h-32 w-full object-cover transition group-hover:scale-105"
                />
                <div className="p-2">
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-emerald-600 text-emerald-600" />
                    <p className="text-xs font-semibold text-emerald-700">{(product?.rating ?? 0).toFixed(1)}</p>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900">{product?.nameKey ? t(product.nameKey) : 'Untitled'}</p>
                  <p className="mt-1 text-xs text-emerald-700">✓ Purchased</p>
                </div>
              </Link>
            ))}
          </div>
        </article>
      )}

      {(cartItems?.length ?? 0) > 0 && (
        <article className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-slate-900">Items in Cart</h3>
            <Link to="/dashboard/cart" className="text-xs font-semibold text-orange-600 hover:text-orange-700">
              View cart →
            </Link>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cartItems?.slice(0, 3).map((product) => (
              <Link
                key={product?.id}
                to={`/products/${product?.id}`}
                className="group overflow-hidden rounded-lg border border-orange-200 bg-orange-50 transition hover:border-orange-400 hover:shadow-md"
              >
                <img
                  src={product?.imageUrl || ''}
                  alt={product?.nameKey ? t(product.nameKey) : 'Product'}
                  className="h-32 w-full object-cover transition group-hover:scale-105"
                />
                <div className="p-2">
                  <p className="text-xs font-semibold text-orange-600">{product?.categoryKey ? t(product.categoryKey) : 'Item'}</p>
                  <p className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900">{product?.nameKey ? t(product.nameKey) : 'Untitled'}</p>
                  <p className="mt-1 text-sm font-bold text-orange-700">$ {(product?.price ?? 0).toFixed(0)}</p>
                </div>
              </Link>
            ))}
          </div>
        </article>
      )}

      {(favoriteProducts?.length ?? 0) > 0 && (
        <article className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-slate-900">Your Favorites</h3>
            <Link to="/dashboard/wishlist" className="text-xs font-semibold text-orange-600 hover:text-orange-700">
              View all →
            </Link>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {favoriteProducts?.slice(0, 3).map((product) => (
              <Link
                key={product?.id}
                to={`/products/${product?.id}`}
                className="group overflow-hidden rounded-lg border border-rose-200 bg-rose-50 transition hover:border-rose-400 hover:shadow-md"
              >
                <img
                  src={product?.imageUrl || ''}
                  alt={product?.nameKey ? t(product.nameKey) : 'Product'}
                  className="h-32 w-full object-cover transition group-hover:scale-105"
                />
                <div className="p-2">
                  <div className="flex items-center gap-1">
                    <Heart className="h-3 w-3 fill-rose-600 text-rose-600" />
                    <p className="text-xs font-semibold text-rose-700">{(product?.rating ?? 0).toFixed(1)}</p>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900">{product?.nameKey ? t(product.nameKey) : 'Untitled'}</p>
                  <p className="mt-1 text-xs text-rose-700">♥ Favorite</p>
                </div>
              </Link>
            ))}
          </div>
        </article>
      )}

      {(myBooks?.length ?? 0) === 0 &&
        (purchasedProducts?.length ?? 0) === 0 &&
        (cartItems?.length ?? 0) === 0 &&
        (favoriteProducts?.length ?? 0) === 0 && (
          <article className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <p className="text-sm text-slate-500">No activity yet. Start exploring and building your library!</p>
            <Link
              to="/products"
              className="mt-4 inline-block rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
            >
              Browse Products
            </Link>
          </article>
        )}
    </div>
  );
};

export default CustomerOverview;
